import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [business, setBusiness] = useState(null); // Legacy: first/active business
  const [businesses, setBusinesses] = useState([]); // All user businesses
  const [activeBusiness, setActiveBusiness] = useState(null); // Currently selected business
  const [loading, setLoading] = useState(true);

  // Memoize checkAuth to prevent infinite effect loops in consumers
  const checkAuth = useCallback(async () => {
    if (!BACKEND_URL) {
      console.warn('REACT_APP_BACKEND_URL is not set');
      setLoading(false);
      return;
    }
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      setUser(response?.data || null);
      setBusiness(response?.data?.business || null);
      
      // Set businesses array
      if (response?.data?.businesses) {
        setBusinesses(response.data.businesses);
        // Set active business
        const activeId = response?.data?.active_business_id;
        const active = activeId 
          ? response.data.businesses.find(b => b.business_id === activeId)
          : response.data.businesses[0];
        setActiveBusiness(active || null);
      } else {
        setBusinesses([]);
        setActiveBusiness(null);
      }
    } catch (error) {
      setUser(null);
      setBusiness(null);
      setBusinesses([]);
      setActiveBusiness(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Memoize refreshUser as well
  const refreshUser = useCallback(async () => {
    await checkAuth();
  }, [checkAuth]);

  const selectProfile = async (profile) => {
    try {
      await axios.post(`${API}/auth/select-profile`, { profile }, {
        withCredentials: true
      });
      setUser(prev => ({ ...prev, active_profile: profile }));
      return true;
    } catch (error) {
      console.error('Failed to select profile:', error);
      return false;
    }
  };

  const switchBusiness = async (businessId) => {
    try {
      const response = await axios.post(`${API}/user/businesses/select`, 
        { business_id: businessId },
        { withCredentials: true }
      );
      
      // Update active business
      const selected = businesses.find(b => b.business_id === businessId);
      if (selected) {
        setActiveBusiness(selected);
        setBusiness(selected); // Legacy compatibility
      }
      
      return response.data;
    } catch (error) {
      console.error('Failed to switch business:', error);
      return null;
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, {
        withCredentials: true
      });
      setUser(null);
      setBusiness(null);
      setBusinesses([]);
      setActiveBusiness(null);
      window.location.href = '/';
    } catch (error) {
      console.error('Logout failed:', error);
      setUser(null);
      setBusiness(null);
      setBusinesses([]);
      setActiveBusiness(null);
      window.location.href = '/';
    }
  };

  // Helper functions
  const isUser = () => user?.account_type === 'user' || user?.account_type === 'both';
  const isBusiness = () => user?.account_type === 'business' || user?.account_type === 'both';
  const isPlatformAdmin = () => user?.is_platform_admin === true;
  const isBusinessVerified = () => activeBusiness?.verification_status === 'verified' || business?.verification_status === 'approved';
  const activeProfile = () => user?.active_profile || 'user';
  const hasMultipleBusinesses = () => businesses.length > 1;

  return (
    <AuthContext.Provider value={{ 
      user, 
      setUser, 
      business,
      setBusiness,
      businesses,
      setBusinesses,
      activeBusiness,
      setActiveBusiness,
      switchBusiness,
      loading, 
      checkAuth,
      refreshUser,
      logout,
      selectProfile,
      isUser,
      isBusiness,
      isPlatformAdmin,
      isBusinessVerified,
      activeProfile,
      hasMultipleBusinesses
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
